import { useCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { useFormatPrice } from "@/hooks/use-format-price";
import { SEARCH_COPY, matchingTopics, topicLabel, searchTours } from "@/lib/search-options";
import { priceUnitShort } from "@/lib/catalog-presentation";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Search, MapPin, Loader2, X, Clock, Sparkles, MessageCircle } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { listUnifiedCatalogLite } from "@/lib/catalog.functions";
import { useActiveLang } from "@/hooks/use-active-lang";
import { productsForCity, normalize } from "@/lib/tour-search";
import { correctQuery, matchDestinations, similarDestinations } from "@/lib/search-fuzzy";
import { SITE } from "@/lib/site";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  inline?: boolean;
  initialQuery?: string;
};

const POPULAR: { slug: string; name: string }[] = [
  { slug: "cancun", name: "Cancún" },
  { slug: "holbox", name: "Holbox" },
  { slug: "isla-mujeres", name: "Isla Mujeres" },
  { slug: "cozumel", name: "Cozumel" },
  { slug: "tulum", name: "Tulum" },
  { slug: "bacalar", name: "Bacalar" },
  { slug: "chichen-itza", name: "Chichén Itzá" },
  { slug: "merida", name: "Mérida" },
];

const TXT = {
  es: { title: "Buscar", ph: "¿A dónde quieres ir? Holbox, cenotes…", close: "Cerrar", popular: "Destinos populares", recent: "Búsquedas recientes", clear: "Borrar", dest: "Destinos", tours: "Tours", loading: "Cargando…", error: "No pudimos cargar los tours. Intenta de nuevo.", none: "No encontramos resultados para", similar: "Quizá buscabas", maya: "Preguntar a Maya", wa: "WhatsApp", all: "Ver todos los resultados", toursIn: "Tours en" },
  en: { title: "Search", ph: "Where to? Holbox, cenotes…", close: "Close", popular: "Popular destinations", recent: "Recent searches", clear: "Clear", dest: "Destinations", tours: "Tours", loading: "Loading…", error: "We couldn't load tours. Please try again.", none: "No results for", similar: "Did you mean", maya: "Ask Maya", wa: "WhatsApp", all: "See all results", toursIn: "Tours in" },
  fr: { title: "Rechercher", ph: "Où aller ? Holbox, cénotes…", close: "Fermer", popular: "Destinations populaires", recent: "Recherches récentes", clear: "Effacer", dest: "Destinations", tours: "Excursions", loading: "Chargement…", error: "Impossible de charger les excursions.", none: "Aucun résultat pour", similar: "Vouliez-vous dire", maya: "Demander à Maya", wa: "WhatsApp", all: "Voir tous les résultats", toursIn: "Excursions à" },
  it: { title: "Cerca", ph: "Dove vuoi andare? Holbox, cenote…", close: "Chiudi", popular: "Destinazioni popolari", recent: "Ricerche recenti", clear: "Cancella", dest: "Destinazioni", tours: "Tour", loading: "Caricamento…", error: "Impossibile caricare i tour.", none: "Nessun risultato per", similar: "Forse cercavi", maya: "Chiedi a Maya", wa: "WhatsApp", all: "Vedi tutti i risultati", toursIn: "Tour a" },
  de: { title: "Suchen", ph: "Wohin? Holbox, Cenoten…", close: "Schließen", popular: "Beliebte Reiseziele", recent: "Letzte Suchen", clear: "Löschen", dest: "Reiseziele", tours: "Touren", loading: "Wird geladen…", error: "Touren konnten nicht geladen werden.", none: "Keine Ergebnisse für", similar: "Meinten Sie", maya: "Maya fragen", wa: "WhatsApp", all: "Alle Ergebnisse", toursIn: "Touren in" },
  pt: { title: "Buscar", ph: "Para onde? Holbox, cenotes…", close: "Fechar", popular: "Destinos populares", recent: "Buscas recentes", clear: "Limpar", dest: "Destinos", tours: "Passeios", loading: "Carregando…", error: "Não foi possível carregar os passeios.", none: "Nenhum resultado para", similar: "Você quis dizer", maya: "Perguntar à Maya", wa: "WhatsApp", all: "Ver todos os resultados", toursIn: "Passeios em" },
} as const;

const RECENT_KEY = "mm_recent_searches";

function readRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
    return Array.isArray(v) ? v.filter((s) => typeof s === "string").slice(0, 5) : [];
  } catch {
    return [];
  }
}
function saveRecent(term: string) {
  try {
    const list = [term, ...readRecent().filter((s) => normalize(s) !== normalize(term))].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* almacenamiento bloqueado: ignorar */
  }
}

/** Nombre corto: corta subtítulos tras « - », « | » o «:». */
function shortTitle(t: string): string {
  const cut = t.split(/\s[|–—-]\s|:\s/)[0]?.trim() ?? t;
  return cut.length >= 8 ? cut : t;
}

export function SearchDialog({ open, onOpenChange, inline = false, initialQuery = "" }: Props) {
  useCurrency(); // subscribe: formatPrice reads the currency store
  const lang = useActiveLang();
  const L = TXT[(lang as keyof typeof TXT)] ?? TXT.es;
  const [q, setQ] = useState(initialQuery);
  const formatPrice = useFormatPrice();
  const extra = SEARCH_COPY[lang];
  const rootRef = useRef<HTMLDivElement>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const navigatingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    navigatingRef.current = false;
    if (!inline) setQ(initialQuery);
    setRecent(readRecent());
    if (inline) return;
    // Botón "atrás" del navegador/teléfono cierra la búsqueda.
    history.pushState({ mmSearch: true }, "");
    let closedByBack = false;
    const onPop = () => {
      closedByBack = true;
      onOpenChange(false);
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      if (!closedByBack && !navigatingRef.current && history.state?.mmSearch) history.back();
    };
  }, [open, onOpenChange, inline, initialQuery]);

  useEffect(() => {
    if (!inline || !open) return;
    const outside = (e: PointerEvent) => { if (!rootRef.current?.contains(e.target as Node)) onOpenChange(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [inline, open, onOpenChange]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["catalog", "lite", lang, "search-dialog"],
    queryFn: () => listUnifiedCatalogLite({ data: { lang } }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const term = q.trim();
  const { tours, destinations } = useMemo(() => {
    if (!term) return { tours: [], destinations: [] };
    const all = data?.tours ?? [];
    const found = searchTours(all, term);
    return { tours: found.slice(0, 8), destinations: matchDestinations(term, 8) };
  }, [data, term]);

  const topics = matchingTopics(term);
  const noResults = !!term && !isLoading && !isError && tours.length === 0 && destinations.length === 0 && topics.length === 0;
  const similar = useMemo(() => (noResults ? similarDestinations(term) : []), [noResults, term]);

  const close = () => onOpenChange(false);

  const goDestination = (slug: string, name: string) => {
    saveRecent(name);
    navigatingRef.current = true;
    close();
    navigate({ to: "/tours/$city", params: { city: slug } });
  };

  const submit = () => {
    if (!term) return;
    saveRecent(term);
    navigatingRef.current = true;
    close();
    navigate({ to: "/tours", search: { q: term }, replace: !inline });
  };

  const chip =
    "inline-flex min-h-[40px] items-center gap-1.5 rounded-full border border-border bg-background px-4 text-sm font-medium text-foreground transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const heading = "px-1 pb-2 mm-eyebrow text-muted-foreground";

  const content = (
    <>
        <form
          onSubmit={(e) => { e.preventDefault(); submit(); }}
          className="flex h-16 shrink-0 rounded-2xl items-center gap-3 border-b border-border bg-background px-4 sm:px-5"
        >
          <Search className="h-5 w-5 shrink-0 text-primary" aria-hidden />
          <input
            ref={inputRef}
            autoFocus={!inline}
            type="search"
            value={q}
            onFocus={() => { if (inline) onOpenChange(true); }}
            onKeyDown={(e) => { if (e.key === "Escape") close(); }}
            onChange={(e) => { setQ(e.target.value); if (inline) onOpenChange(true); }}
            aria-label={L.title}
            enterKeyHint="search"
            autoComplete="off"
            placeholder={L.ph}
            className="h-full min-w-0 flex-1 border-0 bg-transparent text-base font-medium text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
          />
          <Button type="submit" className="shrink-0 rounded-full">{L.title}</Button>
          {open && <Button
            variant="ghost"
            type="button"
            onClick={close}
            aria-label={L.close}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-foreground/70 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" />
          </Button>}
        </form>

        {open && <div id="hero-search-suggestions" className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 pb-[calc(env(safe-area-inset-bottom,0px)+24px)] sm:p-5">
          {!term && (
            <>
              <p className={heading}>{L.popular}</p>
              <div className="flex flex-wrap gap-2">
                {POPULAR.filter(d => productsForCity(data?.tours ?? [], d.slug).length > 0).map((d) => (
                  <Button variant="ghost" key={d.slug} type="button" className={chip} onClick={() => goDestination(d.slug, d.name)}>
                    <MapPin className="h-3.5 w-3.5 text-primary" aria-hidden /> {d.name}
                  </Button>
                ))}
              </div>
              {recent.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between">
                    <p className={heading}>{L.recent}</p>
                    <Button variant="ghost"
                      type="button"
                      className="pb-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        try { localStorage.removeItem(RECENT_KEY); } catch { /* */ }
                        setRecent([]);
                      }}
                    >
                      {L.clear}
                    </Button>
                  </div>
                  <ul className="grid gap-1">
                    {recent.map((r) => (
                      <li key={r}>
                        <Button variant="ghost"
                          type="button"
                          onClick={() => setQ(r)}
                          className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-2 text-left text-sm text-foreground hover:bg-accent"
                        >
                          <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                          <span className="truncate">{r}</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {term && isLoading && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {L.loading}
            </div>
          )}
          {term && isError && (
            <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">{L.error}</div>
          )}

          {destinations.length > 0 && (
            <section className="mb-5">
              <p className={heading}>{L.dest}</p>
              <ul className="grid gap-1">
                {destinations.map((d) => (
                  <li key={d.slug}>
                    <Button variant="ghost"
                      type="button"
                      onClick={() => goDestination(d.slug, d.name)}
                      className="flex min-h-[48px] w-full justify-start items-center gap-3 rounded-xl px-2 text-left hover:bg-accent"
                    >
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                        <MapPin className="h-4 w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-foreground">{L.toursIn} {d.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{d.region} · {productsForCity(data?.tours ?? [], d.slug).length} {extra.count}</span>
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(["attractions", "categories"] as const).map(kind => {
            const rows = topics.filter(topic => topic.kind === kind);
            return rows.length > 0 && <section key={kind} className="mb-5">
              <p className={heading}>{extra[kind]}</p>
              {rows.map(topic => <Button key={topic.id} variant="ghost" className="flex min-h-11 w-full justify-start" onClick={() => {
                saveRecent(topicLabel(topic, lang)); navigatingRef.current = true; close(); navigate({ to: "/tours", search: { cat: topic.id } });
              }}><Search />{topicLabel(topic, lang)}</Button>)}
            </section>;
          })}

          {tours.length > 0 && (
            <section>
              <p className={heading}>{L.tours}</p>
              <ul className="grid gap-1">
                {tours.map((tr) => {
                  const slug = tr.slug ?? String(tr.id);
                  const image = tr.images?.[0];
                  return (
                    <li key={`${tr.source}-${tr.id}`} className="min-w-0">
                      <Link
                        to="/tour/$slug"
                        params={{ slug }}
                        onClick={() => {
                          saveRecent(term);
                          navigatingRef.current = true;
                          close();
                        }}
                        className="flex min-h-[64px] items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-accent"
                      >
                        <span className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-muted">
                          {image ? <img src={image} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">{shortTitle(tr.title)}</span>
                          <span className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                            {tr.location && <span className="min-w-0 truncate">{tr.location}</span>}
                            {tr.priceFrom != null && (
                              <span className="ml-auto shrink-0 whitespace-nowrap font-semibold text-primary">
                                {formatPrice(tr.priceFrom, tr.currency)} <span className="font-normal text-muted-foreground">{priceUnitShort(tr as never, lang)}</span>
                              </span>
                            )}
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <Button variant="ghost"
                type="button"
                onClick={submit}
                className="mt-3 w-full rounded-xl border border-border py-3 text-sm font-semibold text-primary hover:bg-primary/5"
              >
                {L.all}
              </Button>
            </section>
          )}

          {noResults && (
            <div className="rounded-2xl border border-dashed border-border p-5 text-center">
              <p className="text-sm text-foreground">
                {L.none} <strong>«{term}»</strong>
              </p>
              {similar.length > 0 && (
                <>
                  <p className="mt-4 text-xs text-muted-foreground">{L.similar}</p>
                  <div className="mt-2 flex flex-wrap justify-center gap-2">
                    {similar.map((d) => (
                      <Button variant="ghost" key={d.slug} type="button" className={chip} onClick={() => goDestination(d.slug, d.name)}>
                        {d.name}
                      </Button>
                    ))}
                  </div>
                </>
              )}
              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                <Link
                  to="/maya"
                  onClick={close}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
                >
                  <Sparkles className="h-4 w-4" /> {L.maya}
                </Link>
                <a
                  href={`${SITE.contact.whatsappUrl}?text=${encodeURIComponent(`Hola Mostrando México, busco: ${term}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-border px-4 text-sm font-semibold text-foreground hover:bg-accent"
                >
                  <MessageCircle className="h-4 w-4" /> {L.wa}
                </a>
              </div>
            </div>
          )}
        </div>}
    </>
  );
  if (inline) return <div ref={rootRef} className="relative z-40 rounded-2xl border border-border bg-background text-foreground shadow-card">
    {content.props.children[0]}
    {open && <div className="absolute inset-x-0 top-full mt-2 max-h-[60dvh] overflow-y-auto rounded-xl border border-border bg-popover shadow-[var(--shadow-elevated)]">{content.props.children[1]}</div>}
  </div>;
  return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent
    onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); }}
    className="inset-0 left-0 top-0 flex h-[100dvh] max-h-none w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 md:inset-auto md:left-1/2 md:top-[72px] md:h-auto md:max-h-[80dvh] md:max-w-3xl md:-translate-x-1/2 md:rounded-xl md:border [&>button:last-child]:hidden">
    <DialogTitle className="sr-only">{L.title}</DialogTitle>{content}
  </DialogContent></Dialog>;

}
